import React, { ReactNode } from 'react';
import styled from 'styled-components';

import { sharedCopy } from '../../../data/static-content/shared/shared-content';
import {
  ARTICLE_TYPES,
  BUTTON_TYPES,
  CARD_WRAPPER_TYPES,
  CONTENTFUL_COMPONENT,
  ICON_TYPES,
} from '../../../shared/constants';
import { IArticle, ICard } from '../../../types/shared';

import ArticlePreviewsWrapper from '../../atoms/article-previews-wrapper/ArticlePreviewsWrapper';
import SecondaryButton from '../../atoms/secondary-button/SecondaryButton';

import { getInternalLink } from '../../../utils/generic';
import { BUTTON_LABELS, languageHelper } from '../../../utils/language';
import { parseSectionID } from '../../../utils/string-utils';
import { getTagsByTypes } from '../../../utils/tags';

import mq from '../../../styles/breakpoints';
import { CardTitleCss, IntroText } from '../../../styles/generic';
import Spacings from '../../../styles/Spacings';

import { TagTypes } from '../../../tokens';
import ArticlePreviewCard from '../article-preview-card/ArticlePreviewCard';

interface Props {
  articles: IArticle[] | ICard[];
  children: ReactNode | ReactNode[];
  anchorName?: string;
}

const FeaturedArticlesWrapper = ({ articles, children, anchorName }: Props) => {
  const id = parseSectionID(
    CARD_WRAPPER_TYPES.featuredArticlesWrapper,
    anchorName
  );
  const { lang } = languageHelper();
  const contentOverviewPage = articles?.length
    ? (articles[0] as IArticle).parentPage
    : '/magazine';
  const articlePreviewHeight = articles?.length >= 4 ? 'small' : 'large';

  return (
    <Container id={id}>
      <Filters>
        <Title data-testid="title">{children}</Title>
        <SecondaryButton
          to={`/${getInternalLink(contentOverviewPage)}`}
          label={BUTTON_LABELS.viewArticles[lang]}
          type={BUTTON_TYPES.internal}
          icon={ICON_TYPES.chevron}
        />
      </Filters>
      {articles && articles.length ? (
        <FogContainer>
          <ArticlePreviewsWrapper>
            {articles.map((article, index) => {
              const articleData = article as IArticle;
              const cardData = article as ICard;
              if (article.__typename === CONTENTFUL_COMPONENT.articlePage)
                return (
                  <ArticlePreviewCard
                    key={`content-preview-card-${index}`}
                    title={articleData.title}
                    image={articleData.mainImage.gatsbyImageData}
                    contentType={articleData.articleType}
                    tags={getTagsByTypes(articleData.tags || [], [
                      TagTypes.SECTOR,
                      TagTypes.CATEGORY,
                    ])}
                    data={articleData}
                    articleType={ARTICLE_TYPES.internal}
                    cardType={articleData?.previewCardType}
                    imageHeight={articlePreviewHeight}
                  />
                );
              if (article.__typename === CONTENTFUL_COMPONENT.card)
                return (
                  <ArticlePreviewCard
                    image={cardData.image?.gatsbyImageData}
                    contentType={cardData.subtitle as string}
                    title={cardData.title}
                    tags={[
                      {
                        id: '0',
                        name: cardData.subtitle || '',
                        value: cardData.subtitle || '',
                      },
                    ]}
                    primaryLink={cardData.primaryLink}
                    articleType={ARTICLE_TYPES.external}
                    key={`content-preview-card-${index}`}
                  />
                );
            })}
          </ArticlePreviewsWrapper>
        </FogContainer>
      ) : (
        <ArticlePreviewsWrapper>
          <IntroText data-testid="no-articles">
            {sharedCopy.cardWrappers.noArticles[lang]}
          </IntroText>
        </ArticlePreviewsWrapper>
      )}
    </Container>
  );
};

export default FeaturedArticlesWrapper;

const Container = styled.div`
  width: 100%;
`;

const FogContainer = styled.div`
  position: relative;
`;

const Title = styled.h2`
  ${CardTitleCss};
  margin: 0 0 ${Spacings.XS};

  ${mq.from.breakpoint.XL`
     margin: 0;
  `}
`;

const Filters = styled.div`
  align-items: center;
  box-sizing: border-box;
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: space-between;
  margin: 0 auto ${Spacings.M};
  padding: 0;
  width: 100%;

  ${mq.from.breakpoint.M`
    margin: 0 auto ${Spacings.S};
  `}

  ${mq.from.breakpoint.L`
    margin: 0 auto ${Spacings.M};
  `}
`;
